import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  emptyRule,
  resolveRuleStatus,
  sortRoleRuleVersions,
  sortRoles,
  type FunctionRole,
  type RoleRule,
  type RoleRuleVersion
} from '../models/functionRoles';

/** Custom-role count at which the soft-cap consolidation banner appears. */
export const SOFT_CAP_THRESHOLD = 20;

export type SettingsFunctionRolesStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

export type CreateRoleStatus = 'closed' | 'open' | 'saving';

/** Save lifecycle for the rename-confirm modal. `idle` = no modal; `confirm` =
 *  modal open (label changed, awaiting user confirm). RPC-in-flight is
 *  tracked separately via `isSaving`. */
export type SaveStatus = 'idle' | 'confirm';

/** Delete lifecycle for the selected custom role. */
export type DeleteStatus = 'idle' | 'blocked' | 'confirm' | 'deleting';

/** Archive lifecycle for the selected role's agent rule. */
export type ArchiveStatus = 'idle' | 'confirm' | 'saving';

/** Editable draft of a role's three editable fields. The agent rule is the
 *  markdown blob; label + description are the role-metadata. */
export interface RoleDraft {
  label: string;
  description: string;
  agentRule: string;
}

/** Inline error for the create-role modal — `field` targets the input. */
export interface CreateRoleError {
  field: 'key' | 'label' | 'form';
  message: string;
}

export interface CreateRoleInput {
  key: string;
  label: string;
  description: string;
  agentRule: string;
}

export const ROLE_KEY_PATTERN = /^[a-z0-9-]{2,32}$/;
export const RESERVED_ROLE_KEYS = [
  'engineer',
  'designer',
  'product',
  'qa',
  'other'
] as const;

const KEY_FORMAT_ERROR = 'Key must be 2–32 lowercase chars: a–z, 0–9, dash.';
const RESERVED_KEY_ERROR = 'That key is reserved for system roles.';
const KEY_TAKEN_ERROR = 'Another role already uses this key.';
const LABEL_ERROR = 'Label must be 1–40 chars.';
const NETWORK_ERROR = 'Something went wrong. Try again.';
const FORBIDDEN_ERROR = 'System roles cannot be deleted.';
const LABEL_MISMATCH_ERROR =
  "Typed label doesn't match. Type the role's label exactly to confirm.";
const SAVE_RULE_ERROR = "Couldn't save the agent rule. Try again.";
const ARCHIVE_RULE_ERROR = "Couldn't archive the rule. Try again.";
const RESTORE_RULE_ERROR = "Couldn't restore the rule. Try again.";
const RESTORE_VERSION_ERROR = "Couldn't restore the version. Try again.";

interface FunctionRoleRow {
  id: string;
  key: string;
  label: string;
  description: string | null;
  kind: 'system' | 'custom';
}

interface RoleRuleRow {
  id: string;
  function_role_id: string;
  content: string;
  last_propagated_at: string | null;
  archived_at: string | null;
}

interface RoleRuleVersionRow {
  id: string;
  role_rule_id: string;
  version_number: number;
  content: string;
  restored_from_version_id: string | null;
  created_at: string;
  created_by: string | null;
}

interface MemberRosterRow {
  kind: string;
  function_role_label: string | null;
}

interface ProfileRow {
  id: string;
  display_name: string | null;
  username: string | null;
}

function toVersion(row: RoleRuleVersionRow): RoleRuleVersion {
  return {
    id: row.id,
    roleRuleId: row.role_rule_id,
    versionNumber: row.version_number,
    content: row.content,
    restoredFromVersionId: row.restored_from_version_id,
    createdAt: row.created_at,
    createdBy: row.created_by
  };
}

function ruleFromRow(row: RoleRuleRow | undefined): RoleRule {
  if (!row) return emptyRule();
  return {
    ruleId: row.id,
    content: row.content,
    lastPropagatedAt: row.last_propagated_at,
    archivedAt: row.archived_at
  };
}

export interface UseSettingsFunctionRolesVM {
  status: SettingsFunctionRolesStatus;
  orgId: string | null;
  orgName: string | null;
  viewerRole: ViewerRoleSystemKey;
  canManage: boolean;
  systemRoles: FunctionRole[];
  customRoles: FunctionRole[];
  archivedRoles: FunctionRole[];
  selectedId: string | null;
  selectedRole: FunctionRole | null;
  selectedRuleVersions: RoleRuleVersion[];
  handles: Record<string, string>;
  error: string | null;
  createStatus: CreateRoleStatus;
  createError: CreateRoleError | null;
  draft: RoleDraft | null;
  /** The value the agent-rule textarea should render — draft when set, else
   *  the saved content. */
  editorRule: string;
  isDirty: boolean;
  isLabelDirty: boolean;
  isRuleDirty: boolean;
  saveStatus: SaveStatus;
  /** True while `confirmSave` RPCs are in flight (direct or via rename modal). */
  isSaving: boolean;
  saveError: string | null;
  deleteStatus: DeleteStatus;
  deleteError: string | null;
  archiveStatus: ArchiveStatus;
  archiveError: string | null;
  restoreError: string | null;
  /** Version id currently being restored (History row), or null. */
  restoringVersionId: string | null;
  includeArchived: boolean;
  customRoleCount: number;
  softCapDismissed: boolean;
  saveToast: string | null;
  deleteToast: string | null;
  archiveToast: string | null;
  restoreToast: string | null;
  actions: {
    select: (id: string) => void;
    openCreate: () => void;
    closeCreate: () => void;
    createRole: (input: CreateRoleInput) => Promise<boolean>;
    editDraft: (patch: Partial<RoleDraft>) => void;
    /** Copy the hardcoded default rule into the agent-rule field. */
    startFromDefault: () => void;
    cancelEdit: () => void;
    /** Click Save. Opens rename-confirm modal when label changed; otherwise
     *  commits straight through. */
    save: () => Promise<boolean>;
    closeRenameConfirm: () => void;
    /** Commit the unified save (called from the modal Save button or directly
     *  when no rename modal needed). */
    confirmSave: () => Promise<boolean>;
    openArchiveConfirm: () => void;
    closeArchiveConfirm: () => void;
    archiveRule: () => Promise<boolean>;
    restoreRule: () => Promise<boolean>;
    restoreRuleVersion: (versionId: string) => Promise<boolean>;
    openDelete: () => void;
    closeDelete: () => void;
    deleteRole: (confirmLabel: string) => Promise<boolean>;
    toggleIncludeArchived: () => void;
    dismissSoftCap: () => void;
    clearSaveToast: () => void;
    clearDeleteToast: () => void;
    clearArchiveToast: () => void;
    clearRestoreToast: () => void;
  };
}

/**
 * ViewModel for the merged `/settings/function-roles` page (2026-05-17).
 *
 * Loads function_roles + role_rules + role_rule_versions + member roster +
 * author profiles for the caller's home org. Each function role carries its
 * 1:1 agent rule + status; the editor edits label / description / agent rule
 * together. Save calls `rename_function_role` (label / description) plus
 * `save_role_rule` (agent rule) as needed; archive / restore / version
 * restore round-trip through the corresponding role-rule RPCs.
 *
 * Role-metadata version history (`function_role_versions`) is no longer read —
 * decision 2026-05-17 dropped that UI surface in favor of agent-rule history.
 */
export function useSettingsFunctionRolesVM(): UseSettingsFunctionRolesVM {
  // The active org is owned by the shell (see `useActiveOrg`). This VM no longer
  // re-derives it from the viewer's oldest membership; it reads whichever org
  // the user switched into and re-runs when that changes.
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const orgId = activeOrgId;
  const orgName = activeOrgName;

  const [status, setStatus] = useState<SettingsFunctionRolesStatus>('loading');
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [roles, setRoles] = useState<FunctionRole[]>([]);
  const [versions, setVersions] = useState<RoleRuleVersion[]>([]);
  const [handles, setHandles] = useState<Record<string, string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createStatus, setCreateStatus] = useState<CreateRoleStatus>('closed');
  const [createError, setCreateError] = useState<CreateRoleError | null>(null);
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatus>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archiveStatus, setArchiveStatus] = useState<ArchiveStatus>('idle');
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [includeArchived, setIncludeArchived] = useState(false);
  const [softCapDismissed, setSoftCapDismissed] = useState(false);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [deleteToast, setDeleteToast] = useState<string | null>(null);
  const [archiveToast, setArchiveToast] = useState<string | null>(null);
  const [restoreToast, setRestoreToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      // Viewer's permission role FOR THE ACTIVE ORG (gates canManage).
      const { data: membershipRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role:permission_role_id(system_key)')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load function roles. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const memRow = (membershipRow ?? null) as {
        permission_role: RoleEmbed;
      } | null;
      if (!memRow) {
        setStatus('empty');
        return;
      }
      const sysKey = Array.isArray(memRow.permission_role)
        ? memRow.permission_role[0]?.system_key ?? null
        : memRow.permission_role?.system_key ?? null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;
      const resolvedOrgId = activeOrgId;

      const [rolesRes, rulesRes, rosterRes, versionsRes] = await Promise.all([
        supabase
          .from('function_roles')
          .select('id, key, label, description, kind')
          .eq('org_id', resolvedOrgId)
          .is('deleted_at', null),
        supabase
          .from('role_rules')
          .select(
            'id, function_role_id, content, last_propagated_at, archived_at'
          )
          .eq('org_id', resolvedOrgId),
        supabase.rpc('list_org_members', { p_org_id: resolvedOrgId }),
        supabase
          .from('role_rule_versions')
          .select(
            'id, role_rule_id, version_number, content, restored_from_version_id, created_at, created_by'
          )
          .eq('org_id', resolvedOrgId)
          .order('version_number', { ascending: false })
      ]);

      if (cancelled) return;
      if (
        rolesRes.error ||
        rulesRes.error ||
        rosterRes.error ||
        versionsRes.error
      ) {
        setStatus('error');
        setError("Couldn't load function roles. Try again.");
        return;
      }

      const roleRows = (rolesRes.data ?? []) as ReadonlyArray<FunctionRoleRow>;
      const ruleRows = (rulesRes.data ?? []) as ReadonlyArray<RoleRuleRow>;
      const rosterRows = (rosterRes.data ??
        []) as ReadonlyArray<MemberRosterRow>;
      const versionRows = (versionsRes.data ??
        []) as ReadonlyArray<RoleRuleVersionRow>;

      // Per-role member count, bucketed by function_role_label (the only key
      // exposed by list_org_members today).
      const countByLabel = new Map<string, number>();
      for (const m of rosterRows) {
        if (m.kind !== 'active') continue;
        const label = m.function_role_label;
        if (!label) continue;
        countByLabel.set(label, (countByLabel.get(label) ?? 0) + 1);
      }

      const ruleByRoleId = new Map<string, RoleRuleRow>();
      for (const r of ruleRows) ruleByRoleId.set(r.function_role_id, r);

      const resolved: FunctionRole[] = roleRows.map((r) => ({
        id: r.id,
        key: r.key,
        label: r.label,
        description: r.description,
        kind: r.kind,
        memberCount: countByLabel.get(r.label) ?? 0,
        agentRule: ruleFromRow(ruleByRoleId.get(r.id))
      }));

      // Resolve author handles for the History column.
      const authorIds = Array.from(
        new Set(
          versionRows
            .map((v) => v.created_by)
            .filter((id): id is string => !!id)
        )
      );
      const handleMap: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', authorIds);
        if (cancelled) return;
        for (const p of (profileRows ?? []) as ReadonlyArray<ProfileRow>) {
          handleMap[p.id] = p.display_name || p.username || 'someone';
        }
      }

      const { system, custom } = sortRoles(resolved);
      const ordered = [...system, ...custom];
      // Preselect Engineer / first non-archived role so the editor opens on
      // something the left list actually shows by default.
      const firstActive =
        ordered.find((r) => resolveRuleStatus(r.agentRule) !== 'archived') ??
        ordered[0];

      setViewerRole(role);
      setRoles(resolved);
      setVersions(versionRows.map(toVersion));
      setHandles(handleMap);
      setSelectedId(firstActive?.id ?? null);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedId) ?? null,
    [roles, selectedId]
  );

  const isDirty = useMemo(() => {
    if (!draft || !selectedRole) return false;
    return (
      draft.label !== selectedRole.label ||
      draft.description !== (selectedRole.description ?? '') ||
      draft.agentRule !== selectedRole.agentRule.content
    );
  }, [draft, selectedRole]);

  const isLabelDirty = useMemo(() => {
    if (!draft || !selectedRole) return false;
    return draft.label !== selectedRole.label;
  }, [draft, selectedRole]);

  const isRuleDirty = useMemo(() => {
    if (!draft || !selectedRole) return false;
    return draft.agentRule !== selectedRole.agentRule.content;
  }, [draft, selectedRole]);

  const editorRule = draft?.agentRule ?? selectedRole?.agentRule.content ?? '';

  const select = useCallback(
    (id: string) => {
      if (id === selectedId) return;
      // Switching roles discards unsaved edits — confirm first.
      if (isDirty && !window.confirm('Discard unsaved changes?')) return;
      setSelectedId(id);
      setDraft(null);
      setSaveStatus('idle');
      setIsSaving(false);
      setSaveError(null);
      setDeleteStatus('idle');
      setDeleteError(null);
      setArchiveStatus('idle');
      setArchiveError(null);
      setRestoreError(null);
    },
    [selectedId, isDirty]
  );

  const openCreate = useCallback(() => {
    setCreateError(null);
    setCreateStatus('open');
  }, []);

  const closeCreate = useCallback(() => {
    setCreateError(null);
    setCreateStatus('closed');
  }, []);

  const createRole = useCallback(
    async (input: CreateRoleInput): Promise<boolean> => {
      if (!orgId) return false;
      const key = input.key.trim().toLowerCase();
      const label = input.label.trim();
      const description = input.description.trim();
      const agentRule = input.agentRule;

      if (!ROLE_KEY_PATTERN.test(key)) {
        setCreateError({ field: 'key', message: KEY_FORMAT_ERROR });
        return false;
      }
      if ((RESERVED_ROLE_KEYS as readonly string[]).includes(key)) {
        setCreateError({ field: 'key', message: RESERVED_KEY_ERROR });
        return false;
      }
      if (label.length < 1 || label.length > 40) {
        setCreateError({ field: 'label', message: LABEL_ERROR });
        return false;
      }

      setCreateError(null);
      setCreateStatus('saving');

      const { data, error: rpcErr } = await supabase.rpc(
        'create_function_role',
        {
          p_org_id: orgId,
          p_key: key,
          p_label: label,
          p_description: description
        }
      );

      if (rpcErr) {
        setCreateError({ field: 'form', message: NETWORK_ERROR });
        setCreateStatus('open');
        return false;
      }

      const envelope = data as
        | { ok: true; role: FunctionRoleRow }
        | { ok: false; error: string }
        | null;

      if (!envelope || envelope.ok !== true) {
        const code = envelope && !envelope.ok ? envelope.error : '';
        if (code === 'invalid_key') {
          setCreateError({ field: 'key', message: KEY_FORMAT_ERROR });
        } else if (code === 'reserved_key') {
          setCreateError({ field: 'key', message: RESERVED_KEY_ERROR });
        } else if (code === 'key_taken') {
          setCreateError({ field: 'key', message: KEY_TAKEN_ERROR });
        } else if (code === 'invalid_label') {
          setCreateError({ field: 'label', message: LABEL_ERROR });
        } else {
          setCreateError({ field: 'form', message: NETWORK_ERROR });
        }
        setCreateStatus('open');
        return false;
      }

      const row = envelope.role;
      const newRole: FunctionRole = {
        id: row.id,
        key: row.key,
        label: row.label,
        description: row.description,
        kind: row.kind,
        memberCount: 0,
        agentRule: emptyRule()
      };
      setRoles((prev) => [...prev, newRole]);
      setSelectedId(newRole.id);
      setCreateError(null);
      setCreateStatus('closed');

      // If the user authored an agent rule in the create modal, persist it
      // immediately so the new role lands `Authored`.
      if (agentRule.trim() !== '') {
        const { data: ruleData } = await supabase.rpc('save_role_rule', {
          p_function_role_id: newRole.id,
          p_content: agentRule
        });
        const ruleEnv = ruleData as
          | { ok: true; rule: RoleRuleRow }
          | { ok: false; error: string }
          | null;
        if (ruleEnv && ruleEnv.ok === true) {
          const persisted = ruleEnv.rule;
          setRoles((prev) =>
            prev.map((r) =>
              r.id === newRole.id
                ? { ...r, agentRule: ruleFromRow(persisted) }
                : r
            )
          );
          // Reload versions so the History disclosure shows the v1 snapshot.
          await reloadVersions(orgId);
        }
      }
      return true;
    },
    // reloadVersions is a stable closure over orgId — referenced directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId]
  );

  const editDraft = useCallback(
    (patch: Partial<RoleDraft>) => {
      setSaveError(null);
      setDraft((prev) => {
        const base: RoleDraft = prev ?? {
          label: selectedRole?.label ?? '',
          description: selectedRole?.description ?? '',
          agentRule: selectedRole?.agentRule.content ?? ''
        };
        return { ...base, ...patch };
      });
    },
    [selectedRole]
  );

  const startFromDefault = useCallback(() => {
    editDraft({});
    setDraft((prev) => {
      const base: RoleDraft = prev ?? {
        label: selectedRole?.label ?? '',
        description: selectedRole?.description ?? '',
        agentRule: ''
      };
      return {
        ...base,
        agentRule:
          'Read the document end-to-end before responding. Cite the document by name when proposing changes. Prefer questions over assumptions when intent is unclear.'
      };
    });
  }, [editDraft, selectedRole]);

  const cancelEdit = useCallback(() => {
    setDraft(null);
    setSaveStatus('idle');
    setIsSaving(false);
    setSaveError(null);
  }, []);

  const closeRenameConfirm = useCallback(() => {
    setSaveStatus('idle');
    setIsSaving(false);
  }, []);

  /** Re-fetch role_rule_versions for the org after a write so the History
   *  disclosure stays current. Best-effort — failure leaves prior list. */
  const reloadVersions = useCallback(
    async (forOrg?: string | null) => {
      const target = forOrg ?? orgId;
      if (!target) return;
      const { data, error: vErr } = await supabase
        .from('role_rule_versions')
        .select(
          'id, role_rule_id, version_number, content, restored_from_version_id, created_at, created_by'
        )
        .eq('org_id', target)
        .order('version_number', { ascending: false });
      if (vErr || !data) return;
      const rows = data as ReadonlyArray<RoleRuleVersionRow>;
      setVersions(rows.map(toVersion));

      const seen = Array.from(
        new Set(
          rows.map((v) => v.created_by).filter((id): id is string => !!id)
        )
      );
      const missing = seen.filter((id) => !(id in handles));
      if (missing.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', missing);
        if (profileRows) {
          setHandles((prev) => {
            const next = { ...prev };
            for (const p of profileRows as ReadonlyArray<ProfileRow>) {
              next[p.id] = p.display_name || p.username || 'someone';
            }
            return next;
          });
        }
      }
    },
    [orgId, handles]
  );

  /** Persist label / description / agent rule for the selected role. Called
   *  directly when no rename modal is needed, or by the rename-confirm modal
   *  Save button. */
  const confirmSave = useCallback(async (): Promise<boolean> => {
    if (!selectedRole || !draft) return false;
    const role = selectedRole;
    const label = draft.label.trim();
    const description = draft.description.trim();
    const agentRule = draft.agentRule;

    if (label.length < 1 || label.length > 40) {
      setSaveError(LABEL_ERROR);
      if (isLabelDirty) setSaveStatus('confirm');
      return false;
    }

    setSaveError(null);
    setIsSaving(true);

    const metadataDirty =
      label !== role.label || description !== (role.description ?? '');
    const ruleDirty = agentRule !== role.agentRule.content;

    let nextLabel = role.label;
    let nextDescription = role.description;
    if (metadataDirty) {
      const { data, error: rpcErr } = await supabase.rpc(
        'rename_function_role',
        {
          p_role_id: role.id,
          p_label: label,
          p_description: description
        }
      );
      if (rpcErr) {
        setSaveError(NETWORK_ERROR);
        if (isLabelDirty) setSaveStatus('confirm');
        setIsSaving(false);
        return false;
      }
      const envelope = data as
        | { ok: true; role: FunctionRoleRow }
        | { ok: false; error: string }
        | null;
      if (!envelope || envelope.ok !== true) {
        const code = envelope && !envelope.ok ? envelope.error : '';
        setSaveError(code === 'invalid_label' ? LABEL_ERROR : NETWORK_ERROR);
        if (isLabelDirty) setSaveStatus('confirm');
        setIsSaving(false);
        return false;
      }
      nextLabel = envelope.role.label;
      nextDescription = envelope.role.description;
    }

    let nextRule: RoleRule = role.agentRule;
    if (ruleDirty) {
      const { data, error: rpcErr } = await supabase.rpc('save_role_rule', {
        p_function_role_id: role.id,
        p_content: agentRule
      });
      if (rpcErr) {
        setSaveError(SAVE_RULE_ERROR);
        if (isLabelDirty) setSaveStatus('confirm');
        setIsSaving(false);
        return false;
      }
      const envelope = data as
        | { ok: true; rule: RoleRuleRow }
        | { ok: false; error: string }
        | null;
      if (!envelope || envelope.ok !== true) {
        setSaveError(SAVE_RULE_ERROR);
        if (isLabelDirty) setSaveStatus('confirm');
        setIsSaving(false);
        return false;
      }
      nextRule = ruleFromRow(envelope.rule);
    }

    setRoles((prev) =>
      prev.map((r) =>
        r.id === role.id
          ? {
              ...r,
              label: nextLabel,
              description: nextDescription,
              agentRule: nextRule
            }
          : r
      )
    );
    setDraft(null);
    setSaveStatus('idle');
    setIsSaving(false);
    setSaveError(null);
    setSaveToast('Saved.');
    if (ruleDirty) {
      void reloadVersions();
    }
    return true;
  }, [selectedRole, draft, isLabelDirty, reloadVersions]);

  const save = useCallback(async (): Promise<boolean> => {
    if (!selectedRole || !draft) return false;
    const label = draft.label.trim();
    if (label.length < 1 || label.length > 40) {
      setSaveError(LABEL_ERROR);
      return false;
    }
    if (isLabelDirty) {
      setSaveError(null);
      setSaveStatus('confirm');
      return false;
    }
    return confirmSave();
  }, [selectedRole, draft, isLabelDirty, confirmSave]);

  const openArchiveConfirm = useCallback(() => {
    setArchiveError(null);
    setArchiveStatus('confirm');
  }, []);

  const closeArchiveConfirm = useCallback(() => {
    setArchiveStatus('idle');
  }, []);

  const setRuleArchived = useCallback(
    async (archived: boolean): Promise<boolean> => {
      if (!selectedRole) return false;
      const role = selectedRole;
      const ruleId = role.agentRule.ruleId;
      if (!ruleId) {
        // Can only archive a rule that's been saved at least once.
        setArchiveError(
          archived ? 'Save the rule before archiving.' : 'No rule to restore.'
        );
        return false;
      }
      setArchiveError(null);
      setArchiveStatus('saving');

      const { data, error: rpcErr } = await supabase.rpc(
        'set_role_rule_archived',
        { p_role_rule_id: ruleId, p_archived: archived }
      );
      if (rpcErr) {
        setArchiveError(archived ? ARCHIVE_RULE_ERROR : RESTORE_RULE_ERROR);
        setArchiveStatus('idle');
        return false;
      }
      const envelope = data as
        | { ok: true; rule: RoleRuleRow }
        | { ok: false; error: string }
        | null;
      if (!envelope || envelope.ok !== true) {
        setArchiveError(archived ? ARCHIVE_RULE_ERROR : RESTORE_RULE_ERROR);
        setArchiveStatus('idle');
        return false;
      }
      const nextRule = ruleFromRow(envelope.rule);
      setRoles((prev) =>
        prev.map((r) =>
          r.id === role.id ? { ...r, agentRule: nextRule } : r
        )
      );
      setArchiveStatus('idle');
      setArchiveToast(archived ? 'Rule archived.' : 'Rule restored.');
      void reloadVersions();
      return true;
    },
    [selectedRole, reloadVersions]
  );

  const archiveRule = useCallback(() => setRuleArchived(true), [setRuleArchived]);
  const restoreRule = useCallback(() => setRuleArchived(false), [setRuleArchived]);

  const restoreRuleVersion = useCallback(
    async (versionId: string): Promise<boolean> => {
      if (!selectedRole) return false;
      const role = selectedRole;
      setRestoreError(null);
      setRestoringVersionId(versionId);

      const { data, error: rpcErr } = await supabase.rpc(
        'restore_role_rule_version',
        { p_version_id: versionId }
      );
      setRestoringVersionId(null);

      if (rpcErr) {
        setRestoreError(RESTORE_VERSION_ERROR);
        return false;
      }
      const envelope = data as
        | { ok: true; rule: RoleRuleRow }
        | { ok: false; error: string }
        | null;
      if (!envelope) {
        setRestoreError(RESTORE_VERSION_ERROR);
        return false;
      }
      if (envelope.ok !== true) {
        if (envelope.error === 'already_at_version') {
          setRestoreToast('Already at this version.');
          return true;
        }
        setRestoreError(RESTORE_VERSION_ERROR);
        return false;
      }
      const nextRule = ruleFromRow(envelope.rule);
      setRoles((prev) =>
        prev.map((r) =>
          r.id === role.id ? { ...r, agentRule: nextRule } : r
        )
      );
      // Drop any working copy so the editor shows the restored content.
      setDraft(null);
      setRestoreToast('Restored.');
      void reloadVersions();
      return true;
    },
    [selectedRole, reloadVersions]
  );

  const openDelete = useCallback(() => {
    if (!selectedRole) return;
    setDeleteError(null);
    setDeleteStatus(selectedRole.memberCount > 0 ? 'blocked' : 'confirm');
  }, [selectedRole]);

  const closeDelete = useCallback(() => {
    setDeleteStatus('idle');
    setDeleteError(null);
  }, []);

  const deleteRole = useCallback(
    async (confirmLabel: string): Promise<boolean> => {
      if (!selectedRole) return false;
      setDeleteError(null);
      setDeleteStatus('deleting');

      const { data, error: rpcErr } = await supabase.rpc(
        'delete_function_role',
        { p_role_id: selectedRole.id, p_confirm_label: confirmLabel }
      );

      if (rpcErr) {
        setDeleteError(NETWORK_ERROR);
        setDeleteStatus('confirm');
        return false;
      }
      const envelope = data as
        | { ok: true; role_id: string; label: string }
        | { ok: false; error: string; member_count?: number }
        | null;
      if (!envelope || envelope.ok !== true) {
        const code = envelope && !envelope.ok ? envelope.error : '';
        if (code === 'members_assigned') {
          setDeleteError(null);
          setDeleteStatus('blocked');
        } else if (code === 'label_mismatch') {
          setDeleteError(LABEL_MISMATCH_ERROR);
          setDeleteStatus('confirm');
        } else if (code === 'forbidden') {
          setDeleteError(FORBIDDEN_ERROR);
          setDeleteStatus('confirm');
        } else {
          setDeleteError(NETWORK_ERROR);
          setDeleteStatus('confirm');
        }
        return false;
      }
      const removedId = envelope.role_id;
      const removedLabel = envelope.label;
      setRoles((prev) => {
        const next = prev.filter((r) => r.id !== removedId);
        const engineer = next.find(
          (r) => r.kind === 'system' && r.key === 'engineer'
        );
        setSelectedId(engineer?.id ?? next[0]?.id ?? null);
        return next;
      });
      setDraft(null);
      setSaveStatus('idle');
      setSaveError(null);
      setDeleteStatus('idle');
      setDeleteError(null);
      setDeleteToast(removedLabel);
      return true;
    },
    [selectedRole]
  );

  const toggleIncludeArchived = useCallback(() => {
    setIncludeArchived((prev) => !prev);
  }, []);

  const dismissSoftCap = useCallback(() => setSoftCapDismissed(true), []);
  const clearSaveToast = useCallback(() => setSaveToast(null), []);
  const clearDeleteToast = useCallback(() => setDeleteToast(null), []);
  const clearArchiveToast = useCallback(() => setArchiveToast(null), []);
  const clearRestoreToast = useCallback(() => setRestoreToast(null), []);

  const { systemRoles, customRoles, archivedRoles } = useMemo(() => {
    const { system, custom } = sortRoles(roles);
    const isArchived = (r: FunctionRole) =>
      resolveRuleStatus(r.agentRule) === 'archived';
    return {
      systemRoles: system.filter((r) => !isArchived(r)),
      customRoles: custom.filter((r) => !isArchived(r)),
      archivedRoles: [...system, ...custom].filter(isArchived)
    };
  }, [roles]);

  const selectedRuleVersions = useMemo(() => {
    const ruleId = selectedRole?.agentRule.ruleId;
    if (!ruleId) return [];
    return sortRoleRuleVersions(
      versions.filter((v) => v.roleRuleId === ruleId)
    );
  }, [selectedRole, versions]);

  const customRoleCount = useMemo(
    () => roles.filter((r) => r.kind === 'custom').length,
    [roles]
  );

  const canManage = viewerRole === 'owner' || viewerRole === 'admin';

  return {
    status,
    orgId,
    orgName,
    viewerRole,
    canManage,
    systemRoles,
    customRoles,
    archivedRoles,
    selectedId,
    selectedRole,
    selectedRuleVersions,
    handles,
    error,
    createStatus,
    createError,
    draft,
    editorRule,
    isDirty,
    isLabelDirty,
    isRuleDirty,
    saveStatus,
    isSaving,
    saveError,
    deleteStatus,
    deleteError,
    archiveStatus,
    archiveError,
    restoreError,
    restoringVersionId,
    includeArchived,
    customRoleCount,
    softCapDismissed,
    saveToast,
    deleteToast,
    archiveToast,
    restoreToast,
    actions: {
      select,
      openCreate,
      closeCreate,
      createRole,
      editDraft,
      startFromDefault,
      cancelEdit,
      save,
      closeRenameConfirm,
      confirmSave,
      openArchiveConfirm,
      closeArchiveConfirm,
      archiveRule,
      restoreRule,
      restoreRuleVersion,
      openDelete,
      closeDelete,
      deleteRole,
      toggleIncludeArchived,
      dismissSoftCap,
      clearSaveToast,
      clearDeleteToast,
      clearArchiveToast,
      clearRestoreToast
    }
  };
}
